import { apiService } from '@/feature/api/axios';
import type { IServerListQueryParams } from '@/shared/types/serverPagination';
import { handleApiError } from '@/shared/utils/errorUtils';

import type {
  IUser,
  IUsersListResponse,
  IAccountsListResponse,
  ICreateUserRequest,
} from '../types/user';

export const userService = {
  getUsers: async (): Promise<IUser[]> => {
    try {
      const api = apiService();
      const response = await api.get<IUsersListResponse | IUser[]>(
        '/api/ai-automation-admin/accounts/'
      );

      if (Array.isArray(response)) {
        return response;
      }

      if ('users' in response && Array.isArray(response.users)) {
        return response.users;
      }

      return [];
    } catch (error) {
      handleApiError(error, 'Failed to fetch users. Please try again.');
      return [];
    }
  },

  getUserById: async (id: string): Promise<IUser | null> => {
    try {
      const api = apiService();
      const response = await api.get<IUser>(`/api/ai-automation-admin/accounts/${id}/`);
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to fetch user details. Please try again.');
      return null;
    }
  },

  getAccountsByOrg: async (
    orgId: string,
    params?: IServerListQueryParams
  ): Promise<IAccountsListResponse> => {
    const empty: IAccountsListResponse = {
      count: 0,
      users: [],
      pagination: {
        total_count: 0,
        limit: params?.limit ?? 10,
        offset: params?.offset ?? 0,
        has_next: false,
        has_previous: false,
        fetch_all: false,
      },
    };
    try {
      const api = apiService();
      const response = await api.get<IAccountsListResponse>('/api/ai-automation-admin/accounts', {
        org_id: orgId,
        ...(params ?? {}),
      } as Record<string, unknown>);
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to fetch accounts. Please try again.');
      return empty;
    }
  },

  createUser: async (data: ICreateUserRequest): Promise<IUser | null> => {
    try {
      const api = apiService();
      const response = await api.create<ICreateUserRequest, IUser>(
        '/api/ai-automation-admin/accounts/',
        data
      );
      return response;
    } catch (error) {
      handleApiError(error, 'Failed to create user. Please try again.');
      return null;
    }
  },
};
